<script lang="ts" setup>
import type { ComponentInternalInstance           } from 'vue'

import { ElEmpty, ElScrollbar, ElTree             } from 'element-plus'
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { isNumber                                 } from '@sumdoo/shared'
import { type SdTypeTreeProps,  sdTypeTreeEmits, sdTypeTreeProps } from './type-tree'

defineOptions({ name: 'SdTypeTree' })

const props = defineProps(sdTypeTreeProps)
const emit  = defineEmits(sdTypeTreeEmits)

const state = reactive({
    expandKey : '', // 当前展开行的 key
    currentKey: '', // 当前选中的 key
})

const wrapStyle$ = computed(() => {
    return {
        width: isNumber(props.width) ? `${props.width}px` : props.width,
    }
})

// 确保 label, value 含有默认值
const prop$ = computed(() => {
    return {
        ...props?.prop,
        label   : (props.prop && props.prop.label   ) || 'label',
        value   : (props.prop && props.prop.value   ) || 'value',
        icon    : (props.prop && props.prop.icon    ) || 'icon',
        children: (props.prop && props.prop.children) || 'children',
    }
})

const treeRef = ref()
watch(() => props.list, async(newList) => {
    // 无数据
    if (!newList.length) {
        emit('node-click', null, null)
        return
    }

    await nextTick()
    const $tree = treeRef.value
    if (!$tree)
        return

    const isHas = hasKey(newList, props.nodeKey, state.currentKey)
    if (!isHas) {
        state.currentKey = (newList[0] && (newList[0] as any)[props.nodeKey]) || ''
        await nextTick()
    }

    const node = $tree.getNode(state.currentKey)
    emit('node-click', node?.data, node)

    await nextTick()
    $tree.setCurrentKey(state.currentKey)
}, { immediate: true })

function hasKey(list: SdTypeTreeProps['list'], nodeKey: SdTypeTreeProps['nodeKey'], nodeVal: string | number | boolean) {
    if (!list || !list.length || !nodeKey || !nodeVal)
        return false

    for (const item of list) {
        if ((item as any)[nodeKey] === nodeVal)
            return true
        if (hasKey((item as any).children, nodeKey, nodeVal))
            return  true
    }

    return false
}

// 监听节点点击
function onNodeClick(data: Record<string, any>, node: any, $vm: ComponentInternalInstance) {
    emit('node-click', data, node, $vm)
    state.currentKey = data[props.nodeKey]
}

// 监听节点展开
function onNodeExpand(data: Record<string, any>, node: any, $vm: ComponentInternalInstance) {
    emit('node-expand', data, node, $vm)
    state.expandKey = data[props.nodeKey]
}

// 监听节点收起
function onNodeCollapse(data: Record<string, any>, node: any, $vm: ComponentInternalInstance) {
    emit('node-node-collapse', data, node, $vm)
    const parent = (node as any).parent
    state.expandKey = (parent && parent.data[props.nodeKey]) || ''
}

defineExpose({
    setCurrentKey: treeRef.value?.setCurrentKey,
})

</script>

<template>
    <div class="sd-type-tree" :style="[wrapStyle$]">
        <div v-if="showHead" :class="['sd-type-tree__head sd-clear-fix', headClass]" :style="headStyle">
            <slot v-if="$slots.head" name="head" />
            <div v-else-if="title">
                {{ title }}
            </div>
        </div>
        <ElScrollbar v-if="list.length" class="sd-type-tree__body">
            <div :class="['sd-type-tree__main', mainClass]" :style="mainStyle">
                <ElTree
                    ref="treeRef"
                    :data="list"
                    :render-after-expand="renderAfterExpand"
                    :check-strictly="checkStrictly"
                    :default-expand-all="defaultExpandAll"
                    :expand-on-click-node="expandOnClickNode"
                    :check-on-click-node="checkOnClickNode"
                    :check-descendants="checkDescendants"
                    :auto-expand-parent="autoExpandParent"
                    :default-checked-keys="[state.expandKey]"
                    :default-expanded-keys="defaultExpandedKeys"
                    :current-node-key="currentNodeKey"
                    :render-content="renderContent"
                    :show-checkbox="showCheckbox"
                    :draggable="showCheckbox"
                    :allow-drag="allowDrag"
                    :allow-drop="allowDrop"
                    :props="{ label: prop$.label, children: prop$.children }"
                    :lazy="lazy"
                    :highlight-current="highlightCurrent"
                    :load="load"
                    :filter-node-method="filterNodeMethod"
                    :indent="indent"
                    :icon="icon"
                    :node-key="nodeKey"
                    :accordion="accordion"
                    @node-click="onNodeClick"
                    @node-contextmenu="$emit('node-contextmenu', $event)"
                    @check-change="$emit('check-change', $event)"
                    @check="$emit('check', $event)"
                    @current-change="$emit('current-change', $event)"
                    @node-expand="onNodeExpand"
                    @node-collapse="onNodeCollapse"
                    @node-drag-start="$emit('node-drag-start', $event)"
                    @node-drag-enter="$emit('node-drag-enter', $event)"
                    @node-drag-leave="$emit('node-drag-leave', $event)"
                    @node-drag-over="$emit('node-drag-over', $event)"
                    @node-drag-end="$emit('node-drag-end', $event)"
                    @node-drop="$emit('node-drop', $event)"
                >
                    <template #default="{ data }">
                        <div class="sd-type-tree__content sd-ellipsis sd-ptb-10">
                            <SdIcon v-if="data[prop$.icon]" :name="data[prop$.icon]" />
                            {{ data[prop$.label] }}
                        </div>
                    </template>
                </ElTree>
            </div>
        </ElScrollbar>
        <div v-else class="sd-type-tree__empty">
            <ElEmpty :description="emptyText">
                <slot name="empty" />
            </ElEmpty>
        </div>
        <div v-if="showFoot" :class="['sd-type-tree__foot sd-clear-fix', footClass]" :style="footStyle">
            <slot name="foot" />
        </div>
    </div>
</template>
